import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';
import { LabReportDetail } from '@/components/lab/LabReportDetail';
import { LabProvisionalStar, LabProvisionalStarLegend } from '@/components/lab/LabProvisionalStar';
import { LabResultRowView } from '@/components/lab/LabResultRow';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { compareResultsDateTimeDesc, formatLabDateTime, isComplexMicro, statusLabel } from '@/lib/lab/labDisplay';
import { currentOverviewRows } from '@/lib/lab/labOverviewCurrent';
import { cn } from '@/utils/cn';
import type { LabOverview, LabReportRow } from '@/lib/lab/labTypes';

export function PatientLabResultsTab({
  clinicId,
  patientId,
  revealedCpr,
}: {
  clinicId: string;
  patientId: string;
  revealedCpr: string | null;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<LabOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await callFunction<Record<string, unknown>, LabOverview>('communicate-lab-import', {
          action: 'overview',
          clinic_id: clinicId,
          patient_id: patientId,
        }),
      );
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : t('patients.lab.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [clinicId, patientId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reports = data?.reports ?? [];
  const openReport: LabReportRow | undefined = reports.find((r) => r.id === openId);
  const overviewCurrent = data ? currentOverviewRows(data.current, reports) : [];
  const overviewInvestigations = data ? currentOverviewRows(data.investigations, reports) : [];

  return (
    <PatientTabFrame
      title={t('patients.tabs.lab_results')}
      count={reports.length}
      loading={loading}
      error={error}
      onRetry={() => void load()}
      empty={
        reports.length === 0
          ? {
              icon: <FlaskConical className="h-8 w-8" />,
              title: t('patients.lab.empty'),
              description: t('patients.lab.empty_hint'),
            }
          : null
      }
    >
      {data && reports.length > 0 ? (
        <div className="lab-page mt-s4">
          <section className="lab-section">
            <h3>{t('patients.lab.overview')}</h3>
            <table className="lab-table">
              <thead>
                <tr>
                  <th>{t('patients.lab.analysis')}</th>
                  <th>{t('patients.lab.result')}</th>
                  <th>{t('patients.lab.reference')}</th>
                  <th>{t('patients.lab.producer')}</th>
                  <th>{t('patients.lab.when')}</th>
                </tr>
              </thead>
              <tbody>
                {overviewCurrent
                  .slice()
                  .sort((a, b) => compareResultsDateTimeDesc(a.results_datetime, b.results_datetime))
                  .map((row) => {
                    const report = reports.find((r) => r.id === row.report_id);
                    return (
                      <LabResultRowView
                        key={row.id}
                        row={row}
                        versions={data.versions.filter((v) => v.result_id === row.id)}
                        clinicId={clinicId}
                        objectLinks={(data.object_links ?? []).filter((l) => l.report_id === row.report_id)}
                        consentGiven={report?.consent_given}
                      />
                    );
                  })}
                {overviewInvestigations
                  .slice()
                  .sort((a, b) => compareResultsDateTimeDesc(a.results_datetime, b.results_datetime))
                  .map((inv) => {
                  const report = reports.find((r) => r.id === inv.report_id);
                  const complex = isComplexMicro(
                    inv.examination_summary ?? report?.summary,
                    Boolean(inv.culture_headline) ||
                      data.microscopy.some((m) => m.investigation_id === inv.id),
                  );
                  const corrected = inv.is_corrected || inv.result_status_code === 'svar_rettet';
                  const invVersions = (data.investigation_versions ?? []).filter((v) => v.investigation_id === inv.id);
                  const refused = report?.consent_given === false;
                  return (
                    <tr key={inv.id}>
                      <td>
                        <div className={cn('font-medium', corrected && 'lab-value--corrected')}>
                          {inv.analysis_short_name || inv.mic_analysis_code || 'MIKRO'}
                          {corrected ? ' R' : ''}
                          <LabProvisionalStar code={inv.result_status_code} />
                        </div>
                        <div className="text-xs text-ds-muted">
                          {[inv.analysis_mds_examination, inv.analysis_mds_material].filter(Boolean).join(' · ')}
                        </div>
                        {refused ? (
                          <span className="lab-chip lab-chip--warn mt-s2">{t('patients.lab.consent_refused')}</span>
                        ) : null}
                      </td>
                      <td>
                        {complex ? (
                          <Button type="button" variant="link" onClick={() => setOpenId(inv.report_id)}>
                            MIKRO
                          </Button>
                        ) : (
                          inv.examination_summary || statusLabel(inv.result_status_code)
                        )}
                      </td>
                      <td>—</td>
                      <td>{inv.producer_identifier || '—'}</td>
                      <td>
                        {formatLabDateTime(inv.results_datetime)}
                        {invVersions.length > 0 ? (
                          <ol className="mt-s2 space-y-1 text-xs text-ds-muted">
                            {invVersions.map((v) => (
                              <li key={v.id}>
                                {formatLabDateTime(v.results_datetime)} · {v.examination_summary || statusLabel(v.result_status_code)}
                                {v.result_status_code === 'svar_rettet' ? ' R' : ''}
                              </li>
                            ))}
                          </ol>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <LabProvisionalStarLegend
              codes={[
                ...overviewCurrent.map((row) => row.result_status_code),
                ...overviewInvestigations.map((inv) => inv.result_status_code),
              ]}
            />
          </section>

          <section className="lab-section">
            <h3>{t('patients.lab.reports')}</h3>
            {reports.map((report) => (
              <div key={report.id}>
                <button
                  type="button"
                  className="flex w-full flex-wrap items-baseline justify-between gap-s2 text-left"
                  onClick={() => setOpenId(openId === report.id ? null : report.id)}
                >
                  <span className="font-medium">
                    {report.kind} · {report.sender.organisationName || t('patients.lab.unknown_lab')}
                    {report.is_copy ? ` · ${t('patients.lab.copy')}` : ''}
                    {report.consent_given === false ? ` · ${t('patients.lab.consent_refused')}` : ''}
                  </span>
                  <span className="text-sm text-ds-muted">{formatLabDateTime(report.results_datetime)}</span>
                </button>
                {openReport && openId === report.id ? (
                  <div className="mt-s3">
                    <LabReportDetail
                      report={openReport}
                      data={data}
                      revealedCpr={revealedCpr}
                      clinicId={clinicId}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </section>
        </div>
      ) : null}
    </PatientTabFrame>
  );
}
